import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex items-start gap-3 px-5 pb-3 pt-4"
}
const _hoisted_2 = { class: "min-w-0 flex-1" }
const _hoisted_3 = { class: "truncate text-[17px] font-extrabold leading-tight text-gray-900" }
const _hoisted_4 = { class: "mt-0.5 truncate text-xs text-gray-400" }
const _hoisted_5 = { class: "flex items-center gap-1" }
const _hoisted_6 = ["disabled", "title"]
const _hoisted_7 = {
  key: 0,
  class: "truncate text-xs font-semibold"
}
const _hoisted_8 = ["disabled", "title"]
const _hoisted_9 = {
  key: 0,
  class: "truncate text-xs font-semibold"
}

import { computed, onMounted, onUnmounted } from 'vue';
import { AlertTriangle, ChevronLeft, ChevronRight, X } from 'lucide-vue-next';
import { i18n } from '@/plugins/internationalization/i18n';
import { ALARM_CHIP_CLASS } from './alarmChipStyle';
import { isTopmostDetailSurface, popDetailSurface, pushDetailSurface } from './sectionDetailSurfaces';
import type { SectionRow } from './sectionRow';


export default /*@__PURE__*/_defineComponent({
  __name: 'SectionDetailHeader',
  props: {
    section: {},
    sections: {}
  },
  emits: ["close", "navigate", "handleAlarm"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// Position within the page's display order, matched by id so a refreshed row object still resolves.
const currentIndex = computed(() => (props.section ? props.sections.findIndex((row) => row.id === props.section!.id) : -1));
const prevSection = computed(() => (currentIndex.value > 0 ? props.sections[currentIndex.value - 1] : null));
const nextSection = computed(() => (currentIndex.value !== -1 && currentIndex.value < props.sections.length - 1 ? props.sections[currentIndex.value + 1] : null));

function navigateTo(target: SectionRow | null): void {
    if (target) emit('navigate', target);
}

// Muted meta line under the section name: farm · barn · age (mirrors the table's Alder chip).
const metaLabel = computed(() => {
    if (!props.section) return '';
    const parts = [props.section.farmName, props.section.barnName];
    if (props.section.day !== null) {
        parts.push(
            i18n.global.t('views.monitoring.activeGroups.texts.weekDay', {
                week: props.section.week,
                day: props.section.day,
            })
        );
    }
    return parts.filter(Boolean).join(' · ');
});

// Escape closes; the arrow keys mirror the prev/next buttons. Both are inert while the header is
// closed and while something inside the detail view has a prior claim on the key:
//
//  - an open dropdown or select. Dismissing a menu is not a request to close the whole view, and
//    arrowing through its options is not a request to be moved to another Section behind it.
//  - a focused text field. Its own caret movement and its own Escape come first.
//
// Registered in the capture phase, on window, rather than through onKeyStroke: the popup layers
// close themselves from their own capture-phase listener further down the tree, so a bubble-phase
// handler is only ever reached once the layer it was supposed to defer to has already gone.
function claimedByLayerAbove(event: KeyboardEvent): boolean {
    if (document.querySelector('[data-radix-popper-content-wrapper]')) return true;
    const target = event.target as HTMLElement | null;
    return target instanceof HTMLElement && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));
}

function onKeydownCapture(event: KeyboardEvent): void {
    if (!props.section || !isTopmostDetailSurface(surface) || claimedByLayerAbove(event)) return;
    if (event.key === 'Escape') emit('close');
    if (event.key === 'ArrowLeft') navigateTo(prevSection.value);
    if (event.key === 'ArrowRight') navigateTo(nextSection.value);
}

// Registered on open, released on close, so the shared registry's order matches the order the reader
// opened these surfaces in - see sectionDetailSurfaces.
let surface: symbol;

onMounted(() => {
    surface = pushDetailSurface();
    window.addEventListener('keydown', onKeydownCapture, true);
});

onUnmounted(() => {
    popDetailSurface(surface);
    window.removeEventListener('keydown', onKeydownCapture, true);
});

return (_ctx: any,_cache: any) => {
  return (_ctx.section)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("h2", _hoisted_3, _toDisplayString(_ctx.section.sectionName), 1),
          _createElementVNode("p", _hoisted_4, _toDisplayString(metaLabel.value), 1),
          (_ctx.section.activeAlarms.length > 0)
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                type: "button",
                class: _normalizeClass(["mt-1.5", _unref(ALARM_CHIP_CLASS)]),
                onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('handleAlarm', _ctx.section)))
              }, [
                _createVNode(_unref(AlertTriangle), {
                  class: "h-3 w-3 shrink-0",
                  "aria-hidden": "true"
                }),
                _createTextVNode(" " + _toDisplayString(_ctx.$t('views.monitoring.activeGroups.alarmHandle.button')) + " ", 1),
                _createVNode(_unref(ChevronRight), {
                  class: "h-3 w-3 shrink-0",
                  "aria-hidden": "true"
                })
              ], 2))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("button", {
            type: "button",
            class: "flex max-w-24 items-center gap-1 rounded-lg px-1.5 py-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 sm:max-w-32 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gray-400",
            disabled: !prevSection.value,
            title: prevSection.value?.sectionName,
            onClick: _cache[1] || (_cache[1] = ($event: any) => (navigateTo(prevSection.value)))
          }, [
            _createVNode(_unref(ChevronLeft), {
              class: "h-4 w-4",
              "aria-hidden": "true"
            }),
            (prevSection.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_7, _toDisplayString(prevSection.value.sectionName), 1))
              : _createCommentVNode("", true)
          ], 8, _hoisted_6),
          _createElementVNode("button", {
            type: "button",
            class: "flex max-w-24 items-center gap-1 rounded-lg px-1.5 py-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 sm:max-w-32 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gray-400",
            disabled: !nextSection.value,
            title: nextSection.value?.sectionName,
            onClick: _cache[2] || (_cache[2] = ($event: any) => (navigateTo(nextSection.value)))
          }, [
            (nextSection.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(nextSection.value.sectionName), 1))
              : _createCommentVNode("", true),
            _createVNode(_unref(ChevronRight), {
              class: "h-4 w-4",
              "aria-hidden": "true"
            })
          ], 8, _hoisted_8),
          _createElementVNode("button", {
            type: "button",
            class: "rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600",
            onClick: _cache[3] || (_cache[3] = ($event: any) => (emit('close')))
          }, [
            _createVNode(_unref(X), {
              class: "h-4 w-4",
              "aria-hidden": "true"
            })
          ])
        ])
      ]))
    : _createCommentVNode("", true)
}
}

})